/**
 * Where the assistant can *take* the user, as opposed to what it can answer or
 * change.
 *
 * A third structural category alongside reads (the knowledge registry) and
 * writes (the bulk-edit dry run). A navigation entry can only ever change the
 * URL, and only after an explicit click — it holds no data and stages no
 * mutation — so a misread "how do I…" question is at worst an offer to visit the
 * wrong settings page, never a wrong answer or a wrong listing.
 *
 * Adding a destination is a data change: append a {@link NavigationDestination}
 * with the phrasings it covers and the route it points at. The conversation code
 * does not change. These deliberately intercept before the backend agent, so a
 * plainly navigational question ("how do I update my payment method") is handled
 * here for free instead of falling back to the model.
 */

import { containsFuzzyPhrase, tokenize, tokensSimilar } from './fuzzyMatch';
import type { AssistantAudience } from './assistantCapabilities';

export interface NavigationDestination {
  id: string;
  /** Account types this destination is offered to. */
  audiences: AssistantAudience[];
  /**
   * Contiguous phrasings that identify the request, matched longest-first so a
   * more specific phrase wins over a general one it contains.
   */
  synonyms: string[];
  /**
   * Concept groups, consulted only after every synonym has failed. Every group
   * must appear somewhere in the message, so `[['payment','card'],['update','change']]`
   * covers "update my card" and "change payment" without enumerating each. Kept
   * narrow so a weak word cannot pull an unrelated question in.
   */
  requires?: string[][];
  /** Sentence shown in the conversation above the button. */
  text: string;
  /** Label on the confirm button that performs the navigation. */
  label: string;
  /** Route pushed on confirm. */
  path: string;
}

/**
 * The account settings pages the assistant knows how to reach.
 *
 * Payment method and payout bank account share a route (`/account/payment-methods`,
 * different tab) but are genuinely different concepts — a buyer pays, a supplier
 * gets paid — so they are separate audience-scoped entries with wording that fits
 * each. A supplier who says "update my payment method" still lands on the payout
 * page, because that phrasing is carried on the supplier entry too.
 */
const NAVIGATION_DESTINATIONS: NavigationDestination[] = [
  {
    id: 'paymentMethodBuyer',
    audiences: ['buyer'],
    synonyms: [
      'update my payment method',
      'change my payment method',
      'update payment method',
      'change payment method',
      'update my card',
      'change my card',
      'update my credit card',
      'change my credit card',
      'update my billing',
      'change my billing',
      'billing information',
      'payment method',
      'payment methods',
    ],
    requires: [
      ['payment', 'card', 'billing'],
      ['update', 'change', 'edit', 'manage', 'add'],
    ],
    text: 'You can update your payment methods on the **Payment Methods** page, under Account.',
    label: 'Go to Payment Methods',
    path: '/account/payment-methods',
  },
  {
    id: 'payoutBankSupplier',
    audiences: ['supplier'],
    synonyms: [
      'update my payout account',
      'change my payout account',
      'update my bank account',
      'change my bank account',
      'update my banking',
      'change my banking',
      'where do i get paid',
      'change where i get paid',
      'payout bank account',
      'payout account',
      'update my payment method',
      'change my payment method',
    ],
    requires: [
      ['payout', 'bank', 'banking', 'deposit'],
      ['update', 'change', 'edit', 'manage', 'account'],
    ],
    text: 'You can update your payout bank account on the **Payout Bank Account** page, under Account.',
    label: 'Go to Payout Bank Account',
    path: '/account/payment-methods',
  },
  {
    id: 'notifications',
    audiences: ['buyer', 'supplier'],
    synonyms: [
      'notification settings',
      'notification preferences',
      'update my notifications',
      'change my notifications',
      'manage my notifications',
      'update notifications',
      'change notifications',
      'turn off notifications',
      'turn off emails',
      'stop emails',
      'email preferences',
      'text notifications',
      'opt out of texts',
    ],
    requires: [
      ['notification', 'notifications', 'email', 'emails', 'text', 'texts'],
      ['setting', 'settings', 'preferences', 'update', 'change', 'manage', 'turn', 'stop', 'opt'],
    ],
    text: 'You can choose which emails and texts you receive on the **Notifications** page, under Account.',
    label: 'Go to Notification Settings',
    path: '/account/notification-settings',
  },
  {
    id: 'companyProfile',
    audiences: ['buyer', 'supplier'],
    synonyms: [
      'account settings',
      'account details',
      'company profile',
      'company details',
      'update my company',
      'update company profile',
      'update my account',
      'edit my account',
      'edit my company',
      'change my company name',
      'manage my account',
    ],
    requires: [
      ['account', 'company'],
      ['setting', 'settings', 'profile', 'details', 'update', 'change', 'edit', 'manage'],
    ],
    text: 'You can update your company details on the **Company** page, under Account.',
    label: 'Go to Company Profile',
    path: '/account/company-profile',
  },
  {
    id: 'addDispatcher',
    audiences: ['buyer'],
    synonyms: [
      'add a dispatcher',
      'add dispatcher',
      'how can i add a dispatcher',
      'how do i add a dispatcher',
      'invite a dispatcher',
      'create a dispatcher',
    ],
    text:
      'To add a dispatcher, go to **Users** under Account, click **Add User**, enter their email, choose **Dispatcher** as the role, and save. If you want them to get text notifications, add a mobile number and turn on the SMS checkbox. Your email must be verified before you can add users.',
    label: 'Go to User Management',
    path: '/account/user-management',
  },
  {
    id: 'userManagement',
    audiences: ['buyer', 'supplier'],
    synonyms: [
      'add someone to my account',
      'add a user',
      'add a new user',
      'add another user',
      'add a team member',
      'add a colleague',
      'invite a user',
      'invite someone',
      'invite a team member',
      'manage users',
      'manage my users',
      'manage a user',
      'user management',
      'update a user',
      'update user',
      'edit a user',
      'edit user',
      "update a user's role",
      "change a user's role",
      "update someone's access",
      'remove a user',
      'give someone access',
      'grant access',
    ],
    // Concept-fallback nouns are kept to user-specific terms. Broad pronouns
    // (someone, somebody, people) are deliberately excluded here: paired with the
    // broad verbs below they would route unrelated messages like "update someone
    // about the delay" to User Management. Every pronoun phrasing we do want
    // ("invite someone", "give someone access", "update someone's access") is an
    // explicit synonym above, so it still matches without widening the concept net.
    requires: [
      ['user', 'users', 'member', 'colleague', 'teammate'],
      ['add', 'invite', 'manage', 'remove', 'access', 'grant', 'update', 'edit', 'change'],
    ],
    text: 'You can add or manage the people on your account from the **Users** page, under Account.',
    label: 'Go to User Management',
    path: '/account/user-management',
  },
  {
    id: 'myBookingsBuyer',
    audiences: ['buyer'],
    synonyms: [
      'view my bookings',
      'show me my bookings',
      'go to my bookings',
      'where are my bookings',
      'see my bookings',
      'open my bookings',
      'where can i see my bookings',
      'find my bookings',
      'manage my bookings',
    ],
    // Deliberately narrow verbs — "view/see/open/go/find/manage" — so a count
    // question like "how many bookings do I have" (no such verb) still falls
    // through to the activeBookings/buyerBookingCount skills, and a
    // cancel/extend question falls through to the booking-lifecycle skill.
    // "show" is excluded even though it's a covered synonym below: "how" is
    // one typo away from it, so every "how many bookings…" question would
    // otherwise collide with this destination.
    requires: [
      ['booking', 'bookings', 'reservation'],
      ['view', 'see', 'open', 'go', 'find', 'manage', 'where'],
    ],
    text: 'You can view and manage your bookings from the **Bookings** page.',
    label: 'Go to Bookings',
    path: '/bookings',
  },
  {
    id: 'myBookingsSupplier',
    audiences: ['supplier'],
    synonyms: [
      'view my bookings',
      'show me my bookings',
      'go to my bookings',
      'where are my bookings',
      'see my bookings',
      'open my bookings',
      'where can i see my bookings',
      'find my bookings',
    ],
    requires: [
      ['booking', 'bookings', 'reservation'],
      ['view', 'see', 'open', 'go', 'find', 'where'],
    ],
    text: 'You can view your bookings from the **Bookings** page.',
    label: 'Go to Bookings',
    path: '/supplier-bookings',
  },
  {
    id: 'manageLocationsSupplier',
    audiences: ['supplier'],
    synonyms: [
      'manage my locations',
      'manage my listings',
      'edit my location',
      'edit my listing',
      'add a new location',
      'add a listing',
      'create a new location',
      'create a listing',
      'deactivate a location',
      'deactivate my listing',
      'publish a location',
      'publish my listing',
      'view my locations',
      'view my listings',
    ],
    requires: [
      ['location', 'locations', 'listing', 'listings'],
      ['manage', 'edit', 'add', 'create', 'deactivate', 'publish', 'view'],
    ],
    text: 'You can create, edit, publish, or deactivate your location listings from the **Manage Locations** page.',
    label: 'Go to Manage Locations',
    path: '/locations-profile',
  },
  {
    id: 'favoriteLocationsBuyer',
    audiences: ['buyer'],
    synonyms: [
      'my favorite locations',
      'view my favorite locations',
      'show me my favorite locations',
      'my saved locations',
      'my favorites',
    ],
    requires: [
      ['favorite', 'favorites', 'saved'],
      ['location', 'locations'],
    ],
    text: 'You can see the locations you\u2019ve booked and the ones you\u2019ve saved as favorites on the **My Locations** page.',
    label: 'Go to My Locations',
    path: '/account/my-locations',
  },
  {
    id: 'locationManagerSupplier',
    audiences: ['supplier'],
    // No concept-fallback here, deliberately: "manager" is one typo away from
    // "manage" (see the myBookings destinations above for the same problem),
    // so a fuzzy concept group of ['manager'] would swallow "manage my
    // locations" and other unrelated phrasings. Exact synonyms only.
    synonyms: [
      'what is a location manager',
      'how do i add a location manager',
      'how do i assign a location manager',
      'how do i set a location manager',
      'add a location manager',
      'assign a location manager',
      'set a location manager',
      'change my location manager',
      'update my location manager',
      'location manager',
    ],
    text:
      'A location manager is the person who gets booking requests for a specific location and has ' +
      'a 30-minute window to respond before it auto-declines. To assign one: the person must already ' +
      'be added as a **User** on your account (usually with the Gate Clerk role), then you choose them ' +
      'from the **Location Manager** dropdown when creating or editing that location.',
    label: 'Go to Manage Locations',
    path: '/locations-profile',
  },
  {
    id: 'passwordReset',
    audiences: ['buyer', 'supplier'],
    synonyms: [
      'reset my password',
      'reset password',
      'change my password',
      'change password',
      'update my password',
      'update password',
      'forgot my password',
      'forgot password',
      'new password',
    ],
    requires: [['password', 'passwords']],
    // There is no authenticated "change password" page: /change-password needs a
    // token from the reset email, and lands anyone without one back on /login.
    // The forgot-password flow (enter email, get a reset link) is the real entry
    // point and works whether or not the user is already signed in.
    text: 'You can reset your password from the **Forgot Password** page — enter your email and we will send you a reset link.',
    label: 'Go to Password Reset',
    path: '/forgot-password',
  },
];

/** True when every required concept group appears somewhere in the message. */
const matchesConcepts = (tokens: string[], groups: string[][]): boolean =>
  groups.every((group) =>
    group.some((word) => tokens.some((token) => tokensSimilar(token, word))));

/**
 * Picks the best destination for a message from an explicit candidate list.
 *
 * Longest synonym wins, mirroring the knowledge registry, so a specific phrase
 * beats a general one that contains it. Concept groups are a fallback consulted
 * only when no synonym matched, and can add a match but never steal one.
 */
export const matchNavigationAmong = (
  message: string,
  candidates: NavigationDestination[],
): NavigationDestination | null => {
  const tokens = tokenize(message);

  let best: NavigationDestination | null = null;
  let bestLength = 0;

  for (const destination of candidates) {
    for (const synonym of destination.synonyms) {
      if (synonym.length <= bestLength) continue;
      if (!containsFuzzyPhrase(tokens, tokenize(synonym))) continue;
      best = destination;
      bestLength = synonym.length;
    }
  }

  if (best) return best;

  let conceptMatch: NavigationDestination | null = null;
  let conceptGroups = 0;

  for (const destination of candidates) {
    const groups = destination.requires;
    if (!groups || groups.length <= conceptGroups) continue;
    if (!matchesConcepts(tokens, groups)) continue;
    conceptMatch = destination;
    conceptGroups = groups.length;
  }

  return conceptMatch;
};

/** Finds the settings page a message is asking for, scoped to the account type. */
export const matchNavigation = (
  message: string,
  audience: AssistantAudience,
): NavigationDestination | null =>
  matchNavigationAmong(
    message,
    NAVIGATION_DESTINATIONS.filter((destination) => destination.audiences.includes(audience)),
  );
